export const ALL_OWNERSHIP_FILTERS = [
  "Minhas Solicitações",
  "Outros Usuários",
  "Fila",
  "ChatBots",
  "Agentes de IA",
];

export const STATUS_FILTERS = [
  "Pendente",
  "Em Atendimento",
  "Contato Futuro",
  "Concluída",
  "Perdida",
  "Cancelada",
];

export const UNREAD_FILTER = "Não Lidas";

/** Rótulo do chip/menu com quantidade: "Pendente (10)". Sem count ainda, só o nome. */
export function formatQuickFilterCountLabel(label, count, { capped = false } = {}) {
  if (count == null || count === "") return label;
  const n = Number(count);
  if (!Number.isFinite(n) || n < 0) return label;
  const formatted = Math.floor(n).toLocaleString();
  return `${label} (${formatted}${capped ? "+" : ""})`;
}

const STATUS_LABELS = {
  0: "Pendente",
  1: "Em Atendimento",
  2: "Concluída",
  3: "Contato Futuro",
  4: "Perdida",
  5: "Cancelada",
};

function getStatusLabel(status) {
  if (status == null || status === "") return null;
  const n = Number(status);
  return Number.isFinite(n) && n >= 0 && n <= 5 ? STATUS_LABELS[n] : null;
}

function isAssignedToUser(conversation, userName) {
  return (
    conversation.assignedTo === userName ||
    String(conversation.assignedTo || "").trim() === String(userName || "").trim()
  );
}

function stripHtml(htmlString) {
  if (!htmlString) return "";
  const div = document.createElement("div");
  div.innerHTML = htmlString;
  return (div.textContent || div.innerText || "").trim();
}

function getLastMessageSearchText(conversation) {
  const preview = conversation?.lastMessagePreview;
  if (preview?.text) return String(preview.text).trim();
  return stripHtml(conversation?.lastMessage);
}

export function conversationMatchesLocalSearch(conversation, searchTerm) {
  const q = (searchTerm || "").trim().toLowerCase();
  if (!q) return true;

  if ((conversation?.name || "").toLowerCase().includes(q)) return true;

  const idStr = String(conversation?.id ?? "").toLowerCase();
  const qId = q.startsWith("#") ? q.slice(1) : q;
  if (idStr && (idStr === qId || idStr.includes(qId))) return true;

  const lastMsg = getLastMessageSearchText(conversation).toLowerCase();
  if (lastMsg && lastMsg.includes(q)) return true;

  return false;
}

/**
 * Filtros rápidos da lista: Quem (OR), status (OR) e Não Lidas (AND).
 * "Não Lidas" restringe o resultado — não entra no mesmo OR dos status.
 */
export function conversationMatchesQuickFilters(
  conversation,
  { filters = [], searchTerm = "", userName = "" } = {}
) {
  const c = conversation || {};
  const activeFilters = Array.isArray(filters) ? filters : [];
  const unreadClient = Number(c.unreadClient ?? 0);
  const wantsUnread = activeFilters.includes(UNREAD_FILTER);
  const selectedStatuses = activeFilters.filter((f) => STATUS_FILTERS.includes(f));
  const hasStatusFilter = selectedStatuses.length > 0;
  const hasOwnershipFilter = activeFilters.some((f) =>
    ALL_OWNERSHIP_FILTERS.includes(f)
  );
  const hasAdditionalFilter = wantsUnread || hasStatusFilter;

  const hasUser =
    c.userId != null && c.userId !== "" && c.userId !== 0 && String(c.userId) !== "0";
  const hasAgent =
    c.agentId != null && c.agentId !== "" && c.agentId !== 0 && String(c.agentId) !== "0";
  const userUra = Number(c.userUra) || 0;
  const isAssignedToMe = hasUser && isAssignedToUser(c, userName);
  const isStrictQueue = !hasUser && !hasAgent;
  const isAssignedToOther = hasUser && !hasAgent && !isAssignedToUser(c, userName);
  const isAssignedToAgent = hasAgent;
  const isAssignedToChatBot = hasUser && userUra === 1;

  const matchesOwnership =
    !hasOwnershipFilter ||
    (activeFilters.includes("Minhas Solicitações") && isAssignedToMe) ||
    (activeFilters.includes("Fila") && isStrictQueue) ||
    (activeFilters.includes("Outros Usuários") && isAssignedToOther) ||
    (activeFilters.includes("ChatBots") && isAssignedToChatBot) ||
    (activeFilters.includes("Agentes de IA") && isAssignedToAgent);

  const statusLabel = c.statusLabel ?? getStatusLabel(c.status);
  const matchesStatus =
    !hasStatusFilter ||
    (statusLabel != null && selectedStatuses.includes(statusLabel));
  const matchesUnread = !wantsUnread || unreadClient > 0;
  const matchesSearch = conversationMatchesLocalSearch(c, searchTerm);
  const personalFilterOn = activeFilters.includes("WhatsApp pessoal");

  if (c.kind === "personal") {
    if (!personalFilterOn) return false;
    const personalOwnershipOk =
      !hasOwnershipFilter || activeFilters.includes("Minhas Solicitações");
    if (!personalOwnershipOk) return false;
    return matchesUnread && matchesSearch;
  }

  if (personalFilterOn && !hasOwnershipFilter && !hasAdditionalFilter) return false;

  return matchesOwnership && matchesStatus && matchesUnread && matchesSearch;
}
